import { useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';

// Our components
import Alert from 'components/Alert';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import Input from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import LoanTypeDropdown from 'components/Client/Onboarding/LoanTypeDropdown';
import Dropdown from 'components/Dropdown/Dropdown';
import SoraTextField from 'components/Input/SoraTextField';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';
import { NUMBER, TEXT } from 'components/Input/Types';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Our Query keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our Utils
import { clearFormValues, isSubmissionReady } from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import {
	TYPES_OF_HOME_PROPERTIES,
	CONSTRUCTION,
	NEW_LOAN_OFFER_TYPE
} from 'shared/constants';

function ConstructionForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	loanType,
	loanRequestId,
	financialData,
	postSuccessEvent,
	disableAutoComplete,
	setDisableAutoComplete
}) {
	const queryClient = useQueryClient();

	const updateLoanInquiry = useMutateUpdateLoanInquiry();

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Build full name
	const {
		clientFullName,
		clientId,
		firstName,
		lastName,
		annualIncome,
		creditScoreValue,
		emailAddressValue
	} = clientInfo;

	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');
	const [loanAmount, setLoanAmount] = useState('');
	const [downPaymentPercent, setDownPaymentPercent] = useState('');
	const [propertyType, setPropertyType] = useState('');
	const [primaryUse, setPrimaryUse] = useState('');
	const [desiredLoanTerm, setDesiredLoanTerm] = useState('');
	const [selectedLoanType, setSelectedLoanType] = useState('');

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const requiredSubmissionFields = useMemo(
		() => [
			addressLine1,
			city,
			state,
			zipCode,
			loanAmount,
			desiredLoanTerm,
			selectedLoanType
		],
		[
			addressLine1,
			city,
			state,
			zipCode,
			loanAmount,
			desiredLoanTerm,
			selectedLoanType
		]
	);

	const isFormReady = useMemo(
		() =>
			isClientNameProvided && isSubmissionReady(requiredSubmissionFields),
		[isClientNameProvided, requiredSubmissionFields]
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Construction Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Down payment percent: ${downPaymentPercent}`,
				`Desired loan term: ${desiredLoanTerm}`,
				`Property type: ${propertyType}`,
				`Primary use of property: ${primaryUse}`,
				`Desired loan type: ${selectedLoanType}`,
				`Address for construction: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`
			],
			additionalNotes,
			lendingTypeRequested: 'Construction Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setDesiredLoanTerm,
						setDownPaymentPercent,
						setLoanAmount,
						setSelectedLoanType,
						setPrimaryUse,
						setPropertyType,
						setState,
						setZipCode
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			primaryUse,
			city,
			clientId,
			creditScore: creditScoreValue,
			desiredLoanTerm,
			downPaymentPercent,
			emailAddress: emailAddressValue,
			firstName,
			lastName,
			loanAmount,
			selectedLoanType,
			offerType: NEW_LOAN_OFFER_TYPE,
			propertyType,
			state,
			totalAnnualIncome: annualIncome,
			tradelineType: CONSTRUCTION,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			primaryUse,
			city,
			clientId,
			creditScore: creditScoreValue,
			desiredLoanTerm,
			downPaymentPercent,
			emailAddress: emailAddressValue,
			firstName,
			lastName,
			loanAmount,
			selectedLoanType,
			offerType: NEW_LOAN_OFFER_TYPE,
			propertyType,
			state,
			totalAnnualIncome: annualIncome,
			tradelineType: CONSTRUCTION,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setDesiredLoanTerm,
						setDownPaymentPercent,
						setLoanAmount,
						setSelectedLoanType,
						setPrimaryUse,
						setPropertyType,
						setState,
						setZipCode
					]);

					if (postSuccessEvent) {
						postSuccessEvent();
					}
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: annualIncome,
				label: 'Annual Income:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Loan amount:'
			},
			{
				type: 'percent',
				value: downPaymentPercent,
				label: 'Down payment %:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				${formattedCollection}
				Property Type: ${propertyType}
				Primary Use: ${primaryUse}
				Desired Loan Term: ${desiredLoanTerm}
				Desired Loan Type: ${selectedLoanType}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest();
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Construction Loan
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Loan amount */}
						<CurrencyTextInput
							label="Loan amount"
							subLabel="Amount of loan you would like"
							value={loanAmount}
							onChange={setLoanAmount}
							inputProps={{
								'data-test': 'loanAmount'
							}}
							required
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Down payment % */}
						<Input
							type="percent"
							label="Down payment %"
							helperText="Down payment cannot be empty"
							onChange={setDownPaymentPercent}
							error={downPaymentPercent === ''}
							value={downPaymentPercent}
							inputProps={{
								'data-test': 'downPaymentPercent'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Type of property input */}
						<Input
							label="Type of property (e.g. single home, condo..)"
							value={propertyType}
							onChange={setPropertyType}
							type={TEXT}
							error={propertyType === ''}
							helperText="Type of property cannot be empty"
							inputProps={{
								'data-test': 'propertyType'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Primary use of property */}
						<Dropdown
							items={TYPES_OF_HOME_PROPERTIES}
							selected={primaryUse}
							onChange={setPrimaryUse}
							variant="outlined"
							label="Primary use of property"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Desired loan term */}
						<Input
							label="Desired loan term (# of years)"
							value={desiredLoanTerm}
							onChange={setDesiredLoanTerm}
							type={NUMBER}
							required
							inputProps={{
								'data-test': 'desiredLoanTerm'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<LoanTypeDropdown
							loanType={selectedLoanType}
							setLoanType={setSelectedLoanType}
							gridColumns={12}
							required
						/>
					</Grid>

					<GetFullAddressPartialForm
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
						disableAutoComplete={disableAutoComplete}
						setDisableAutoComplete={setDisableAutoComplete}
						isRequired
					/>
					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

export default ConstructionForm;
